/* Application.js
 *
 * copyright (c) 2010-2026, Christian Mayer and the CometVisu contributors.
 *
 * This program is free software; you can redistribute it and/or modify it
 * under the terms of the GNU General Public License as published by the Free
 * Software Foundation; either version 3 of the License, or (at your option)
 * any later version.
 *
 * This program is distributed in the hope that it will be useful, but WITHOUT
 * ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or
 * FITNESS FOR A PARTICULAR PURPOSE. See the GNU General Public License for
 * more details.
 *
 * You should have received a copy of the GNU General Public License along
 * with this program; if not, write to the Free Software Foundation, Inc.,
 * 59 Temple Place - Suite 330, Boston, MA  02111-1307, USA
 */

/**
 * This is the main application class of the CometVisu.
 *
 * @asset(demo/*)
 * @asset(designs/*)
 * @asset(icons/*)
 * @asset(libs/source-map.min.js)
 * @asset(sentry/*)
 * @asset(test/*)
 *
 * @require(qx.bom.Html,cv.ui.PopupHandler)
 */
qx.Class.define('cv.Application', {
  extend: qx.application.Native,

  /*
  ***********************************************
    CONSTRUCTOR
  ***********************************************
  */
  construct() {
    super();
    this.__appReady = false;
    this.initCommandManager(new qx.ui.command.GroupManager());
    const lang = qx.locale.Manager.getInstance().getLanguage();
    if (qx.io.PartLoader.getInstance().hasPart(lang)) {
      qx.io.PartLoader.require([lang]);
    }
    const pageVis = qx.bom.PageVisibility.getInstance();
    pageVis.addListener('change', () => {
      this.setActive(pageVis.getVisibilityState() === 'visible');
    });

    // install global shortcut for opening the manager
    if (window.parent && typeof window.parent.showManager === 'function') {
      window.showManager = window.parent.showManager;
    } else {
      window.showManager = this.showManager.bind(this);
    }
    if (window.parent && typeof window.parent.showConfigErrors === 'function') {
      window.showConfigErrors = window.parent.showConfigErrors;
    } else {
      window.showConfigErrors = this.showConfigErrors.bind(this);
    }

    // check HTTP server by requesting a small file
    const xhr = new qx.io.request.Xhr('version');
    xhr.set({ method: 'GET'});
    // The version file is plain text (e.g. "0.13.0-dev"), but some proxies
    // serve it with an XML content type. Force a text parser so the response
    // body is never parsed as XML (→ "XML-Verarbeitungsfehler").
    xhr.setParser(response => response);

    const check = e => {
      const req = e.getTarget();
      let header = req.getResponseHeader('Server');
      let isOpenHAB = false;
      if (header) {
        isOpenHAB = header.startsWith('Jetty');
      } else {
        header = req.getResponseHeader('X-CometVisu-Backend-Name');
        isOpenHAB = header === 'openhab';
      }
      this.setServedByOpenhab(isOpenHAB);
      this.setServerChecked(true);
    };
    xhr.addListenerOnce('success', check, this);
    xhr.addListenerOnce('statusError', check, this);
    xhr.addListenerOnce('error', e => {
      const req = e.getTarget();
      this.error('error checking server environment needed to setup the REST url', req.getStatus());
    });
    xhr.send();
  },

  /*
   ******************************************************
   STATICS
   ******************************************************
   */
  statics: {
    consoleCommands: [],
    __commandManager: null,
    /**
     * Controller from the loaded structure injects itself here when loaded
     */
    structureController: null,
    _relResourcePath: null,
    _fullResourcePath: null,

    /**
     * Get the path to the resources.
     * As it is a directory, it will end with a '/'.
     * @param fullPath {boolean?} Get the full path when true
     * @return {string}
     */
    getRelativeResourcePath(fullPath) {
      if (!this._relResourcePath) {
        const baseUrl = window.location.origin + window.location.pathname.split('/').slice(0, -1).join('/');
        this._relResourcePath =
          qx.util.Uri.getAbsolute(qx.util.LibraryManager.getInstance().get('cv', 'resourceUri')).substring(
            baseUrl.length + 1
          );
        if (!this._relResourcePath.endsWith('/')) {
          this._relResourcePath += '/';
        }
      }
      if (fullPath === true) {
        if (!this._fullResourcePath) {
          this._fullResourcePath =
            window.location.pathname.split('/').slice(0, -1).join('/') + '/' + this._relResourcePath;
        }
        return this._fullResourcePath;
      }
      return this._relResourcePath;
    },

    /**
     * Client factory method -> create a client
     * @param {...any} args
     * @return {cv.io.Client|cv.io.Mockup}
     */
    createClient(...args) {
      let Client = cv.io.Client;
      if (
        cv.Config.testMode === true ||
        window.cvTestMode === true ||
        args[0] === 'simulated'
      ) {
        Client = cv.io.Mockup;
      } else if (args[0] === 'openhab') {
        Client = cv.io.openhab.Rest;
        if (
          cv.Config.getStructure() === 'structure-pure' &&
          !cv.Config.pluginsToLoad.includes('plugin-openhab')
        ) {
          cv.Config.configSettings.pluginsToLoad.push('plugin-openhab');
        }
        if (args[1] && args[1].endsWith('/cv/l/')) {
          // we only need the rest path not the login resource
          args[1] = args[1].substring(0, args[1].indexOf('cv/'));
        }
      } else if (args[0] === 'mqtt') {
        Client = cv.io.mqtt.Client;
      }
      return new Client(...args);
    },

    /**
     * Register shortcuts to useful commands the user can execute in the browser console
     * @param shortcutName {String} command name used to install the command in the global namespace
     * @param command {Function} command to execute
     * @param help {String} some documentation about the command
     */
    registerConsoleCommand(shortcutName, command, help) {
      // install command
      if (!(shortcutName in window)) {
        window[shortcutName] = command;
        this.consoleCommands.push(shortcutName + '() - ' + help);
      }
    }
  },

  /*
  ******************************************************
    PROPERTIES
  ******************************************************
  */
  properties: {
    root: {
      nullable: true
    },

    /**
     * true when structure part has been loaded
     */
    structureLoaded: {
      check: 'Boolean',
      init: false,
      event: 'changeStructureLoaded',
      apply: '_applyStructureLoaded'
    },

    commandManager: {
      check: 'qx.ui.command.GroupManager',
      deferredInit: true
    },

    active: {
      check: 'Boolean',
      init: true,
      event: 'changeActive'
    },

    inManager: {
      check: 'Boolean',
      init: false,
      apply: '_applyInManager'
    },

    managerDisabled: {
      check: 'Boolean',
      init: false,
      event: 'changeManagerDisabled'
    },

    managerDisabledReason: {
      check: 'String',
      nullable: true
    },

    managerChecked: {
      check: 'Boolean',
      init: false,
      apply: '_applyManagerChecked'
    },

    /**
     * Mobile device detection (small screen)
     */
    mobile: {
      check: 'Boolean',
      init: false,
      event: 'changeMobile',
      apply: '_applyMobile'
    },

    serverChecked: {
      check: 'Boolean',
      init: false,
      event: 'serverCheckedChanged'
    },

    servedByOpenhab: {
      check: 'Boolean',
      init: false
    },

    serverHasPhpSupport: {
      check: 'Boolean',
      init: false,
      event: 'serverHasPhpSupportChanged'
    },

    serverPhpVersion: {
      check: 'String',
      nullable: true
    },

    server: {
      check: 'String',
      nullable: true
    }
  },

  /*
  *****************************************************************************
     MEMBERS
  *****************************************************************************
  */

  members: {
    _blocker: null,
    __appReady: null,
    __errorNotificationSequence: 0,
    _isCached: null,
    __sourceMapConsumers: null,
    __sourceMapLibraryPromise: null,
    __sourceMapSupportUnavailable: false,

    /**
     * Toggle the {@link qx.bom.Blocker} visibility
     * @param val {Boolean}
     */
    block(val) {
      if (val) {
        if (!this._blocker) {
          this._blocker = new qx.bom.Blocker();
          this._blocker.setBlockerColor('#000000');
          this._blocker.setBlockerOpacity('0.2');
        }
        this._blocker.block();
      } else if (this._blocker) {
        this._blocker.unblock();
      }
    },

    _applyMobile(value) {
      // maintain old value for compatibility
      if (value && !document.body.classList.contains('mobile')) {
        document.body.classList.add('mobile');
      } else if (!value && document.body.classList.contains('mobile')) {
        document.body.classList.remove('mobile');
      }
    },

    isReady() {
      return this.__appReady;
    },

    _applyManagerChecked(value) {
      if (value && cv.Config.loadManager) {
        this.showManager(cv.Config.managerOptions.action, cv.Config.managerOptions.data);
      }
    },

    /**
     * This method contains the initial application code and gets called
     * during startup of the application
     */
    main() {
      cv.ConfigCache.init();
      if (this.isServerChecked()) {
        this._checkBackend();
      } else {
        this.addListenerOnce('serverCheckedChanged', this._checkBackend, this);
      }
      qx.event.GlobalError.setErrorHandler(this.__globalErrorHandler, this);
      if (qx.core.Environment.get('qx.debug')) {
        if (typeof replayLog !== 'undefined' && replayLog) {
          cv.report.Replay.prepare(replayLog);
        }
      }

      document.body.classList.add('loading');

      cv.report.Record.prepare();

      let info =
        '\n' +
        '  _____                     ___      ___\n' +
        ' / ____|                   | \\ \\    / (_)\n' +
        '| |     ___  _ __ ___   ___| |\\ \\  / / _ ___ _   _\n' +
        '| |    / _ \\| \'_ ` _ \\ / _ \\ __\\ \\/ / | / __| | | |\n' +
        '| |___| (_) | | | | | |  __/ |_ \\  /  | \\__ \\ |_| |\n' +
        ' \\_____\\___/|_| |_| |_|\\___|\\__| \\/   |_|___/\\__,_|\n' +
        '-----------------------------------------------------------\n' +
        ' ©2010-' +
        new Date().getFullYear() +
        ' Christian Mayer and the CometVisu contributors.\n' +
        ' Version: ' +
        cv.Version.VERSION +
        '\n';

      if (cv.Application.consoleCommands.length) {
        info += '\n Available commands:\n    ' + cv.Application.consoleCommands.join('\n    ') + '\n';
      }

      info += '-----------------------------------------------------------\n\n';

      // eslint-disable-next-line no-console
      console.log(info);

      // add command to load and open the manager
      const manCommand = new qx.ui.command.Command('Ctrl+M');
      cv.TemplateEngine.getInstance().getCommands().add('open-manager', manCommand);
      manCommand.addListener('execute', () => this.showManager());
      this.registerServiceWorker();

      if (qx.core.Environment.get('qx.aspects')) {
        qx.dev.Profile.stop();
        qx.dev.Profile.start();
      }
      // Call super class
      super.main();
      this.block(true);

      // support native logging capabilities, e.g. Firebug for Firefox
      //noinspection BadExpressionStatementJS,JSHint
      cv.log.appender.Native;

      // Enable logging in debug variant
      if (qx.core.Environment.get('qx.debug')) {
        // support additional cross-browser console. Press F7 to toggle visibility
        //noinspection BadExpressionStatementJS,JSHint
        qx.log.appender.Console;
      }

      /*
       -------------------------------------------------------------------------
       Below is your actual application code...
       -------------------------------------------------------------------------
       */
      // in debug mode load the uncompressed unobfuscated scripts
      qx.bom.Stylesheet.includeFile(
        qx.util.ResourceManager.getInstance().toUri('designs/designglobals.css') +
          (cv.Config.forceReload === true ? '?' + Date.now() : '')
      );

      this.__init();
      if (typeof cv.Config.mobileDevice === 'boolean') {
        this.setMobile(cv.Config.mobileDevice);
      }
      this._onResize(null, true);
      qx.event.Registration.addListener(window, 'resize', this._onResize, this);
    },

    hideManager() {
      if (Object.prototype.hasOwnProperty.call(cv.ui, 'manager')) {
        const ManagerMain = cv.ui['manager']['Main'];
        // only do something when the singleton is already created
        if (ManagerMain.constructor.$$instance) {
          ManagerMain.getInstance().setVisible(false);
        }
      }
    },

    /**
     * @param action {String} manager event that can be handled by cv.ui.manager.Main._onManagerEvent()
     * @param data {String|Map} path of file that action should executed on or a Map of options
     */
    showManager(action, data) {
      if (this.isManagerDisabled()) {
        const notification = {
          topic: 'cv.manager.error',
          target: 'popup',
          title: qx.locale.Manager.tr('Manager is not available'),
          message: this.getManagerDisabledReason(),
          severity: 'high',
          deletable: true
        };

        cv.core.notifications.Router.dispatchMessage(notification.topic, notification);
      } else {
        qx.io.PartLoader.require(
          ['manager'],
          function (states) {
            // break dependency
            const ManagerMain = cv.ui['manager']['Main'];
            const firstCall = !ManagerMain.constructor.$$instance;
            const manager = ManagerMain.getInstance();
            if (!firstCall) {
              manager.setVisible(!manager.getVisible());
            } else {
              // initially bind manager visibility
              manager.bind('visible', this, 'inManager');
            }

            if (manager.getVisible() && action && data) {
              // delay this a little bit, give the manager some time to settle
              qx.event.Timer.once(
                () => {
                  qx.event.message.Bus.dispatchByName('cv.manager.' + action, data);
                },
                this,
                1000
              );
            }
          },
          this
        );
      }
    },

    _applyInManager(value) {
      if (value) {
        qx.bom.History.getInstance().addToHistory('manager', qx.locale.Manager.tr('Manager') + ' - CometVisu');

        this.block(false);
        if (document.body.classList.contains('loading')) {
          document.body.classList.remove('loading');
        }
      } else {
        qx.bom.History.getInstance().addToHistory('', 'CometVisu');
      }
    },

    showConfigErrors(configName, options) {
      configName = configName ? 'visu_config_' + configName + '.xml' : 'visu_config.xml';
      const handlerId = options && options.upgradeVersion ? 'cv.ui.manager.editor.Diff' : 'cv.ui.manager.editor.Source';
      const data = {
        file: configName,
        handler: handlerId,
        handlerOptions: Object.assign(
          {
            jumpToError: true
          },

          options ? options : {}
        )
      };

      if (this.isInManager()) {
        qx.event.message.Bus.dispatchByName('cv.manager.openWith', data);
      } else {
        this.showManager('openWith', data);
      }
      // remove any config error messages shown
      cv.core.notifications.Router.dispatchMessage('cv.config.error', {
        topic: 'cv.config.error',
        condition: false
      });
    },

    validateConfig(configName) {
      const worker = cv.data.FileWorker.getInstance();
      let displayConfigName = configName;
      if (configName) {
        configName = '_' + configName;
      } else {
        configName = '';
        displayConfigName = 'default';
      }
      let notification = {
        topic: 'cv.config.validation',
        severity: 'normal',
        deletable: true,
        unique: true
      };

      cv.core.notifications.Router.dispatchMessage(
        notification.topic,
        Object.assign({}, notification, {
          target: 'toast',
          message: qx.locale.Manager.tr('Validating configuration file...')
        })
      );

      const res = qx.util.ResourceManager.getInstance();
      let configPath = `config/visu_config${configName}.xml`;
      let url = '';
      if (!res.has(configPath) && res.has(`demo/visu_config${configName}.xml`)) {
        url = res.toUri(`demo/visu_config${configName}.xml`);
      }
      if (!url) {
        url = res.toUri(configPath);
      }
      worker
        .validateConfig(url)
        .then(res => {
          // remove the toast information
          cv.core.notifications.Router.dispatchMessage(
            notification.topic,
            Object.assign({}, notification, {
              target: 'toast',
              condition: false
            })
          );

          if (res === true) {
            // show result message as dialog
            cv.core.notifications.Router.dispatchMessage(
              notification.topic,
              Object.assign({}, notification, {
                target: 'popup',
                message: qx.locale.Manager.tr('The %1 configuration has no errors!', displayConfigName),

                icon: 'message_ok'
              })
            );
          } else {
            // show result message as dialog
            qx.log.Logger.error(this, res);
            cv.core.notifications.Router.dispatchMessage(
              notification.topic,
              Object.assign({}, notification, {
                target: 'popup',
                message: qx.locale.Manager.tr('The %1 configuration has %2 errors!', displayConfigName, res.length),

                actions: {
                  link: [
                    {
                      title: qx.locale.Manager.tr('Show errors'),
                      action() {
                        qx.core.Init.getApplication().showConfigErrors(configName);
                      }
                    }
                  ]
                },

                severity: 'high',
                icon: 'message_attention'
              })
            );
          }
        })
        .catch(err => {
          this.error(err);
        });
    },

    __formatErrorPopupMessage(message) {
      return '<pre>' + message + '</pre>';
    },

    __isMappableStackFrame(fileName) {
      return Boolean(fileName) &&
        !['<anonymous>', 'native'].includes(fileName) &&
        !fileName.startsWith('eval') &&
        /\.js(?:[?#]|$)/.test(fileName);
    },

    __parseStackFrame(line) {
      if (!line) {
        return null;
      }

      const trimmed = line.trim();
      let match = /^at\s+(.*?)\s+\((.+):(\d+):(\d+)\)$/.exec(trimmed);
      if (match) {
        return {
          functionName: match[1],
          fileName: match[2],
          lineNumber: parseInt(match[3], 10),
          columnNumber: parseInt(match[4], 10)
        };
      }

      match = /^at\s+(.+):(\d+):(\d+)$/.exec(trimmed);
      if (match) {
        return {
          functionName: null,
          fileName: match[1],
          lineNumber: parseInt(match[2], 10),
          columnNumber: parseInt(match[3], 10)
        };
      }

      match = /^(.*?)@(.+):(\d+):(\d+)$/.exec(trimmed);
      if (match) {
        return {
          functionName: match[1] || null,
          fileName: match[2],
          lineNumber: parseInt(match[3], 10),
          columnNumber: parseInt(match[4], 10)
        };
      }

      return null;
    },

    __normalizeResourceUrl(url) {
      try {
        return new URL(url, window.location.href).toString();
      } catch (error) {
        return null;
      }
    },

    __getSourceMapUrl(scriptUrl) {
      const url = new URL(scriptUrl, window.location.href);
      url.pathname += '.map';
      return url.toString();
    },

    __simplifyMappedSource(source, scriptUrl) {
      const pathMarkers = [
        '/source/class/',
        '/client/source/class/',
        '/node_modules/@qooxdoo/framework/source/class/'
      ];
      for (const marker of pathMarkers) {
        const markerPosition = source.indexOf(marker);
        if (markerPosition !== -1) {
          return source.substring(markerPosition + marker.length);
        }
      }

      try {
        const resolved = new URL(source, scriptUrl);

        if (resolved.origin === window.location.origin) {
          return resolved.pathname.startsWith('/') ? resolved.pathname.substring(1) : resolved.pathname;
        }
        return resolved.toString();
      } catch (error) {
        return source;
      }
    },

    __isFrameworkMappedSource(source) {
      return source.includes('/node_modules/@qooxdoo/framework/source/class/') || source.startsWith('qx/');
    },

    __formatGeneratedFrame(frame) {
      const normalizedUrl = this.__normalizeResourceUrl(frame.fileName);
      let fileName = frame.fileName;
      if (normalizedUrl) {
        try {
          const url = new URL(normalizedUrl);
          fileName = url.pathname.startsWith('/') ? url.pathname.substring(1) : url.pathname;
        } catch (error) {
          fileName = frame.fileName;
        }
      }
      let location = fileName + ':' + frame.lineNumber;
      if (frame.columnNumber !== null) {
        location += ':' + frame.columnNumber;
      }
      return '\t' + (frame.functionName || '<anonymous>') + ' (' + location + ')';
    },

    async __ensureSourceMapSupport() {
      if (window.sourceMap && window.sourceMap.SourceMapConsumer) {
        return true;
      }
      if (this.__sourceMapSupportUnavailable) {
        return false;
      }
      if (!this.__sourceMapLibraryPromise) {
        const scriptUrl = qx.util.ResourceManager.getInstance().toUri('libs/source-map.min.js');
        this.__sourceMapLibraryPromise = cv.util.ScriptLoader
          .includeScript(scriptUrl)
          .then(() => Boolean(window.sourceMap && window.sourceMap.SourceMapConsumer))
          .catch(error => {
            this.debug('failed to load source-map runtime', error);
            this.__sourceMapSupportUnavailable = true;
            return false;
          });
      }
      return this.__sourceMapLibraryPromise;
    },

    async __loadSourceMapConsumer(scriptUrl) {
      const response = await window.fetch(this.__getSourceMapUrl(scriptUrl));
      if (!response.ok) {
        throw new Error('failed to load source map for ' + scriptUrl + ': ' + response.status);
      }
      const mapData = await response.json();
      return new window.sourceMap.SourceMapConsumer(mapData);
    },

    async __getSourceMapConsumer(scriptUrl) {
      const hasSourceMapSupport = await this.__ensureSourceMapSupport();
      if (!hasSourceMapSupport) {
        return null;
      }

      const normalizedUrl = this.__normalizeResourceUrl(scriptUrl);
      if (!normalizedUrl) {
        return null;
      }

      if (!this.__sourceMapConsumers) {
        this.__sourceMapConsumers = {};
      }
      if (!Object.prototype.hasOwnProperty.call(this.__sourceMapConsumers, normalizedUrl)) {
        this.__sourceMapConsumers[normalizedUrl] = this.__loadSourceMapConsumer(normalizedUrl)
          .catch(error => {
            this.debug('failed to load source map', normalizedUrl, error);
            return null;
          });
      }
      return this.__sourceMapConsumers[normalizedUrl];
    },

    async __mapStackFrame(frame) {
      if (!this.__isMappableStackFrame(frame.fileName)) {
        return null;
      }

      const scriptUrl = this.__normalizeResourceUrl(frame.fileName);
      if (!scriptUrl) {
        return null;
      }

      const consumer = await this.__getSourceMapConsumer(scriptUrl);
      if (!consumer) {
        return null;
      }

      const originalPosition = consumer.originalPositionFor({
        line: frame.lineNumber,
        column: Math.max(frame.columnNumber - 1, 0)
      });
      if (!originalPosition || !originalPosition.source || !originalPosition.line) {
        return null;
      }

      return {
        functionName: originalPosition.name || frame.functionName || '<anonymous>',
        source: this.__simplifyMappedSource(originalPosition.source, scriptUrl),
        sourceType: this.__isFrameworkMappedSource(originalPosition.source) ? 'framework' : 'application',
        lineNumber: originalPosition.line,
        columnNumber: originalPosition.column !== null ? originalPosition.column + 1 : null
      };
    },

    async __mapErrorStack(ex) {
      if (!ex || !ex.stack) {
        return null;
      }

      const frames = ex.stack
        .split('\n')
        .map(this.__parseStackFrame, this)
        .filter(Boolean);
      if (frames.length === 0) {
        return null;
      }

      const mappedFrames = [];
      const unresolvedFrames = [];
      for (const frame of frames) {
        const mappedFrame = await this.__mapStackFrame(frame);
        if (mappedFrame) {
          mappedFrames.push(mappedFrame);
        } else if (this.__isMappableStackFrame(frame.fileName)) {
          unresolvedFrames.push(frame);
        }
      }

      const applicationFrames = mappedFrames.filter(frame => frame.sourceType === 'application');
      if (applicationFrames.length > 0) {
        return applicationFrames
          .slice(0, 8)
          .map(frame => {
            let sourceLocation = frame.source + ':' + frame.lineNumber;
            if (frame.columnNumber !== null) {
              sourceLocation += ':' + frame.columnNumber;
            }
            return '\t' + frame.functionName + ' (' + sourceLocation + ')';
          })
          .join('\n');
      }

      if (unresolvedFrames.length > 0) {
        return [
          'No sourcemap entries for the top generated stack frames:',
          unresolvedFrames.slice(0, 4).map(this.__formatGeneratedFrame, this).join('\n')
        ].join('\n');
      }

      if (mappedFrames.length > 0) {
        return [
          'Only framework-internal frames could be resolved:',
          mappedFrames
            .slice(0, 4)
            .map(frame => {
              let sourceLocation = frame.source + ':' + frame.lineNumber;
              if (frame.columnNumber !== null) {
                sourceLocation += ':' + frame.columnNumber;
              }
              return '\t' + frame.functionName + ' (' + sourceLocation + ')';
            })
            .join('\n')
        ].join('\n');
      }

      return null;
    },

    async __updateGlobalErrorNotification(ex, exString, notification, sequence) {
      try {
        const mappedStack = await this.__mapErrorStack(ex);
        if (!mappedStack || sequence !== this.__errorNotificationSequence) {
          return;
        }
        notification.message = this.__formatErrorPopupMessage(exString + '\nMapped Stack:\n' + mappedStack);
        cv.core.notifications.Router.dispatchMessage(notification.topic, notification);
      } catch (error) {
        this.debug('failed to remap stack trace', error);
      }
    },

    __globalErrorHandler(ex) {
      // connect client data for Bug-Report
      let exString = '';
      const maxTraceLength = 2000;
      if (ex.getSourceException && ex.getSourceException()) {
        ex = ex.getSourceException();
      } else if (ex instanceof qx.core.WindowError) {
        exString = ex.toString() + '\nin ' + ex.getUri() + ' line ' + ex.getLineNumber();
      }
      if (!exString) {
        exString = ex.name + ': ' + ex.message;
        if (ex.fileName) {
          exString += '\n in file ' + ex.fileName;
        }
        if (ex.lineNumber) {
          exString += '\n line ' + ex.lineNumber;
        }
        if (ex.description) {
          exString += '\n Description: ' + ex.description;
        }
        try {
          let lastLine = '';
          let repeated = 0;
          let nStack = '';
          qx.dev.StackTrace.getStackTraceFromError(ex).forEach(function (entry) {
            if (lastLine === entry) {
              if (repeated === 0) {
                // count first occurance too
                repeated = 2;
              } else {
                repeated++;
              }
            } else if (repeated > 0) {
              nStack += ' [repeated ' + repeated + ' times]';
              nStack += '\n\t' + entry;
              repeated = 0;
            } else {
              nStack += '\n\t' + entry;
              lastLine = entry;
            }
          }, this);
          if (repeated > 0) {
            nStack += ' [repeated ' + repeated + ' times]';
          }
          if (nStack) {
            exString += '\nNormalized Stack: ' + nStack.substring(0, maxTraceLength) + '\n';
            if (nStack.length > maxTraceLength) {
              exString += 'Stacktrace has been cut off\n';
            }
          }
          if (exString.length + ex.stack.length < maxTraceLength) {
            exString += '\nOriginal Stack: ' + ex.stack + '\n';
          }
        } catch (exc) {
          if (ex.stack) {
            exString += '\nStack: ' + ex.stack.substring(0, maxTraceLength) + '\n';
            if (ex.stack.length > maxTraceLength) {
              exString += 'Stacktrace has been cut off\n';
            }
          }
        }
      }
      const sequence = ++this.__errorNotificationSequence;

      const notification = {
        topic: 'cv.error.' + sequence,
        target: 'popup',
        title: qx.locale.Manager.tr('An error occured'),
        message: this.__formatErrorPopupMessage(exString || ex.stack),
        severity: 'urgent',
        unique: true,
        deletable: false,
        actions: {
          optionGroup: {
            title: qx.locale.Manager.tr('Enable on reload:'),
            options: []
          },

          link: [
            {
              title: qx.locale.Manager.tr('Reload'),
              type: 'reload',
              action(ev) {
                let parent = ev.getTarget().parentNode;
                while (parent) {
                  if (parent.id === 'notification-center' || parent.classList.contains('popup')) {
                    break;
                  }
                  parent = parent.parentNode;
                }
                let box = parent.querySelector('#enableReporting');
                let url = window.location.href.split('#').shift();
                if (box && box.checked) {
                  // reload with reporting enabled
                  url = qx.util.Uri.appendParamsToUrl(url, 'reporting=true');
                }
                box = parent.querySelector('#reportErrors');
                if (box && box.checked) {
                  // reload with automatic error reporting enabled
                  url = qx.util.Uri.appendParamsToUrl(url, 'reportErrors=true');
                }
                cv.util.Location.setHref(url);
              },
              needsConfirmation: false
            }
          ]
        }
      };

      // reload with reporting checkbox
      let link = '';
      if (!cv.Config.reporting) {
        if (qx.locale.Manager.getInstance().getLanguage() === 'de') {
          link =
            ' <a href="https://cometvisu.org/docs/de/latest/manual/config/url-params.html#reporting-session-aufzeichnen" target="_blank" title="Hilfe">(?)</a>';
        }
        notification.actions.optionGroup.options.push({
          title: qx.locale.Manager.tr('Action recording') + link,
          name: 'enableReporting'
        });
      }

      if (qx.core.Environment.get('cv.sentry')) {
        if (window.Sentry) {
          // Sentry has been loaded -> add option to send the error
          notification.actions.link.push({
            title: qx.locale.Manager.tr('Send error to sentry.io'),
            type: 'sentry',
            action() {
              Sentry.captureException(ex);
            },
            needsConfirmation: false,
            deleteMessageAfterExecution: true
          });
        } else {
          link = '';
          if (qx.locale.Manager.getInstance().getLanguage() === 'de') {
            link =
              ' <a href="https://cometvisu.org/docs/de/latest/manual/config/url-params.html#reportErrors" target="_blank" title="Hilfe">(?)</a>';
          }
          notification.actions.optionGroup.options.push({
            title: qx.locale.Manager.tr('Error reporting (on sentry.io)') + link,
            name: 'reportErrors',
            style: 'margin-left: 18px'
          });
        }
      }

      
      cv.core.notifications.Router.dispatchMessage(notification.topic, notification);
      this.__updateGlobalErrorNotification(ex, exString || ex.stack, notification, sequence);
    },

    throwError: qx.core.Environment.select('qx.globalErrorHandling', {
      true() {
        qx.event.Timer.once(() => {
          throw new Error('sourcemap test');
        }, this, 100);
      },
      false: null
    }),

    _onResize() {
      if (cv.Config.mobileDevice === undefined) {
        this.setMobile(window.innerWidth < cv.Config.maxMobileScreenWidth);
      }
    },

    _applyStructureLoaded() {
      if (!cv.Config.cacheUsed) {
        let body = document.querySelector('body');
        // load empty HTML structure
        body.innerHTML = cv.Application.structureController.getHtmlStructure();
      }

      cv.Application.structureController.updateSentryScope();
    },

    /**
     * Internal initialization method
     */
    async __init() {
      window.addEventListener(
        'pagehide',
        () => {
          cv.io.Client.stopAll();
        }
      );

      qx.bom.Lifecycle.onReady(async () => {
        // init notification router
        cv.core.notifications.Router.getInstance();

        this._isCached = false;
        if (cv.Config.enableCache) {
          this._isCached = await cv.ConfigCache.isCached();
        }
        if (this._isCached) {
          // load settings
          this.debug('using cache');
          try {
            // must be awaited, the restored configSettings are needed by the backend client initialization
            await cv.ConfigCache.restore();
          } catch (e) {
            this.error('restoring the config cache failed, parsing the config instead:', e);
            this._isCached = false;
          }
        }
        // initialize NotificationCenter
        cv.ui.NotificationCenter.getInstance();
        cv.ui.ToastManager.getInstance();
        if (!window.cvTestMode && !cv.Config.loadManager) {
          let configLoader = new cv.util.ConfigLoader();
          configLoader.load(this.bootstrap, this);
        }
      });

      // reaction on browser back button
      qx.bom.History.getInstance().addListener('request', e => {
        const anchor = e.getData();
        if (this.isInManager() && anchor !== 'manager') {
          this.hideManager();
        } else if (!this.isInManager() && anchor === 'manager') {
          this.showManager();
        } else if (cv.Application.structureController) {
          cv.Application.structureController.onHistoryRequest(anchor);
        }
      });
    },

    /**
     * Initialize the content
     * @param xml {Document} XML configuration retrieved from backend
     */
    async bootstrap(xml) {
      this.debug('bootstrapping');
      const engine = cv.TemplateEngine.getInstance();
      const loader = cv.util.ScriptLoader.getInstance();

      engine.setConfigSource(xml);
      loader.bind('finished', engine, 'scriptsLoaded');

      if (this._isCached) {
        // check if cache is still valid
        const cacheValid = await cv.ConfigCache.isValid(null, engine.getConfigHash());

        if (!cacheValid) {
          this.debug('cache is invalid re-parse xml');
          // cache invalid
          cv.Config.cacheUsed = false;
          cv.ConfigCache.clear();
          //empty model
          cv.data.Model.getInstance().resetWidgetDataModel();
          cv.data.Model.getInstance().resetAddressList();
        } else {
          // loaded cache is still valid
          cv.report.Record.logCache();
          cv.Config.cacheUsed = true;
          cv.Config.lazyLoading = true;
          cv.io.BackendConnections.initBackendClients();

          // load part for structure
          const structure = cv.Config.getStructure();
          this.debug('loading structure ' + structure);
          engine.loadParts([structure]).then(() => {
            this.loadPlugins();
          });
          engine.addListenerOnce('changeReady', async () => {
            // create the objects
            cv.Config.treePath = await cv.Application.structureController.getInitialPageId();
            const data = cv.data.Model.getInstance().getWidgetData('id_');
            cv.ui.structure.WidgetFactory.createInstance(data.$$type, data);
          });
          // check if the current design settings overrides the cache one
          if (cv.Config.clientDesign && cv.Config.clientDesign !== cv.Config.configSettings.clientDesign) {
            // we have to replace the cached design scripts styles to load
            const styles = [];
            cv.Config.configSettings.stylesToLoad.forEach(function (style) {
              if (typeof style === 'string') {
                styles.push(
                  style.replace('designs/' + cv.Config.configSettings.clientDesign, 'designs/' + cv.Config.clientDesign)
                );
              } else if (typeof style === 'object' && style.uri) {
                style.uri = style.uri.replace('designs/' + cv.Config.configSettings.clientDesign, 'designs/' + cv.Config.clientDesign);
                styles.push(style);
              }
            }, this);
            this.loadStyles(styles);

            const scripts = [];
            cv.Config.configSettings.scriptsToLoad.forEach(function (style) {
              scripts.push(
                style.replace('designs/' + cv.Config.configSettings.clientDesign, 'designs/' + cv.Config.clientDesign)
              );
            }, this);
            this.loadScripts(scripts);
          } else {
            this.loadStyles();
            this.loadScripts();
          }
        }
      }
      if (!cv.Config.cacheUsed) {
        this.debug('start parsing config file');
        const engine = cv.TemplateEngine.getInstance();
        await engine.parse();
        this.loadPlugins();
        this.loadStyles();
        this.loadScripts();
        this.debug('done');

        if (cv.Config.enableCache && cv.Application.structureController.supports('cache')) {
          // cache dom + data when everything is ready
          qx.event.message.Bus.subscribe(
            'setup.dom.finished',
            function () {
              cv.ConfigCache.dump(xml, engine.getConfigHash());
            },
            this
          );
        }
      }
      this.__appReady = true;
    },

    /**
     * Load CSS styles
     * @param styles {Array?}
     */
    loadStyles(styles) {
      if (!styles) {
        styles = cv.Config.configSettings.stylesToLoad;
      }
      if (styles.length) {
        document.body.classList.add('loading-styles');
        const loader = cv.util.ScriptLoader.getInstance();
        loader.addListenerOnce('stylesLoaded', () => {
          document.body.classList.remove('loading');
          document.body.classList.remove('loading-styles');
        });
        loader.addStyles(styles);
      }
    },

    /**
     * Load Javascript source files
     * @param scripts {Array?}
     */
    loadScripts(scripts) {
      if (!scripts) {
        scripts = cv.Config.configSettings.scriptsToLoad;
      }
      if (scripts.length > 0) {
        cv.util.ScriptLoader.getInstance().addScripts(scripts);
      }
    },

    /**
     * Load plugins
     */
    loadPlugins() {
      const plugins = cv.Config.configSettings.pluginsToLoad.slice();
      cv.Config.pluginsToLoad.forEach(name => {
        if (!plugins.includes(name)) {
          plugins.push(name);
        }
      });
      if (plugins.length > 0) {
        const standalonePlugins = [];
        const engine = cv.TemplateEngine.getInstance();
        let partsLoaded = engine.getPartsLoaded();
        let allPluginsQueued = false;
        this.debug('loading plugins');
        if (!partsLoaded) {
          engine.addListener('changePartsLoaded', ev => {
            if (ev.getData() === true) {
              this.debug('plugins loaded');
              partsLoaded = true;
              if (allPluginsQueued) {
                qx.event.Timer.once(
                  function () {
                    cv.util.ScriptLoader.getInstance().setAllQueued(true);
                  },
                  this,
                  0
                );
              }
            }
          });
        }
        const parts = qx.Part.getInstance().getParts();
        const partPlugins = [];
        const path = cv.Application.getRelativeResourcePath();
        plugins.forEach(function (plugin) {
          if (Object.prototype.hasOwnProperty.call(parts, plugin)) {
            partPlugins.push(plugin);
          } else if (!plugin.startsWith('plugin-')) {
            // a real path
            standalonePlugins.push(plugin);
          } else {
            standalonePlugins.push(
              path + 'plugins/' + plugin.replace('plugin-', '') + '/index.js'
            );
          }
        });
        // load part plugins
        engine.loadParts(partPlugins);

        if (standalonePlugins.length > 0) {
          const queueStandalonePlugins = () => {
            allPluginsQueued = true;
            this.debug('loading standalone plugins');
            cv.util.ScriptLoader.getInstance().addScripts(standalonePlugins);

            if (partsLoaded) {
              cv.util.ScriptLoader.getInstance().setAllQueued(true);
            }
          };

          // load standalone plugins after the structure parts has been loaded
          // because they need the classes provided by it
          if (this.getStructureLoaded()) {
            queueStandalonePlugins();
          } else {
            const lid = this.addListener('changeStructureLoaded', ev => {
              if (ev.getData() === true) {
                queueStandalonePlugins();
                this.removeListenerById(lid);
              }
            });
          }
        } else {
          allPluginsQueued = true;
          qx.event.Timer.once(
            function () {
              cv.util.ScriptLoader.getInstance().setAllQueued(true);
            },
            this,
            0
          );
        }
      } else {
        this.debug('no plugins to load => all scripts queued');
        cv.util.ScriptLoader.getInstance().setAllQueued(true);
      }
    },

    __constraintFails(serverVersionId, constraint) {
      const match = /^(>=|<|>|<=|\^)(\d+)\.(\d+)\.?(\d+)?$/.exec(constraint);
      if (match) {
        const operator = match[1];
        const majorConstraint = parseInt(match[2]);
        const hasMinorVersion = match[3] !== undefined;
        const minorConstraint = hasMinorVersion ? parseInt(match[3]) : 0;
        const hasPatchVersion = match[4] !== undefined;
        const patchConstraint = hasPatchVersion ? parseInt(match[4]) : 0;
        const constraintId = 10000 * majorConstraint + 100 * minorConstraint + patchConstraint;
        const maxId =
          10000 * majorConstraint +
          (hasMinorVersion ? 100 * minorConstraint : 999) +
          (hasPatchVersion ? patchConstraint : 99);
        // incomplete implementation of: https://getcomposer.org/doc/articles/versions.md#writing-version-constraints
        switch (operator) {
          case '>=':
            if (serverVersionId < constraintId) {
              return true;
            }
            break;
          case '>':
            if (serverVersionId <= constraintId) {
              return true;
            }
            break;
          case '<=':
            if (serverVersionId > maxId) {
              return true;
            }
            break;
          case '<':
            if (serverVersionId >= maxId) {
              return true;
            }
            break;
          case '^':
            if (serverVersionId < constraintId || serverVersionId > 10000 * (majorConstraint + 1)) {
              return true;
            }
            break;
          case '~':
            if (
              serverVersionId < constraintId || hasPatchVersion
                ? serverVersionId > 10000 * (majorConstraint + 1)
                : serverVersionId > 10000 * majorConstraint + 100 * (patchConstraint + 1)
            ) {
              return true;
            }
            break;
        }
      }
      return false;
    },

    _checkBackend() {
      if (cv.Config.testMode === true) {
        this.setManagerChecked(true);
        this.setServerHasPhpSupport(true);
      } else {
        const isOpenHab = this.isServedByOpenhab();
        const url = isOpenHab ? cv.io.rest.Client.getBaseUrl() + '/environment'
          : cv.io.rest.Client.getBaseUrl().split('/').slice(0, -1).join('/') + '/environment.php';
        const xhr = new qx.io.request.Xhr(url);
        xhr.set({
          method: 'GET',
          accept: 'application/json'
        });
        const failedCheck = (errorText, disableReason) => {
          this.setServerHasPhpSupport(false);
          this.error(errorText);

          this.setManagerDisabled(true);
          this.setManagerDisabledReason(disableReason);
          this.setManagerChecked(true);
        };

        xhr.addListenerOnce('success', e => {
          const req = e.getTarget();
          const env = req.getResponse();
          if (typeof env !== 'object') {
            if (typeof env === 'string' && env.startsWith('<?php')) {
              // no php support
              failedCheck(
                qx.locale.Manager.tr('Disabling manager due to missing PHP support.'),
                qx.locale.Manager.tr('Your server does not support PHP.')
              );
            } else {
              // generic php error
              failedCheck(
                qx.locale.Manager.tr('Disabling manager due to failed PHP request querying the environment.'),
                qx.locale.Manager.tr('Failed PHP request querying the environment.')
              );
            }
          } else {
            // is this is served by native openHAB server, we do not have native PHP support, only the basic
            // rest api is available, but nothing else that needs PHP (like some plugin backend code)
            this.setServerHasPhpSupport(!isOpenHab);

            this.setServerPhpVersion(env.phpversion);
            this.setServer(env.SERVER_SOFTWARE);
            if (Object.prototype.hasOwnProperty.call(env, 'requiresAuth')) {
              cv.io.rest.Client.AUTH_REQUIRED = env.requiresAuth === true;
            }

            const serverVersionId = env.PHP_VERSION_ID;
            const orParts = env.required_php_version
              .split('||')
              .map(e => e.trim());
            const passed = orParts.map(orConstraint => {
              const andParts = orConstraint
                .split(/(\s+|&{2})/)
                .map(e => e.trim());
              // pass when no failed andPart has been found
              return !andParts.some(constraint =>
                this.__constraintFails(serverVersionId, constraint)
              );
            });
            // one of the OR constraints need to pass
            const enable = passed.some(res => res === true);
            if (enable) {
              this.info('Manager available for PHP version', env.phpversion);
            } else {
              this.error(
                'Disabling manager due to PHP version mismatch. Installed:',
                env.phpversion,
                'required:',
                env.required_php_version
              );

              this.setManagerDisabled(true);
              this.setManagerDisabledReason(
                qx.locale.Manager.tr(
                  'Your system does not provide the required PHP version for the manager. Installed: %1, required: %2',
                  env.phpversion,
                  env.required_php_version
                )
              );
            }
            this.setManagerChecked(true);

            if (env.custom_visu_config_xsd_missing === true && !localStorage.getItem('cv.custom_visu_config_xsd_missing')) {
              this.warn('custom_visu_config.xsd is missing, using default one as fallback');
              cv.Config.enableCache = false; // avoid caching the popup notification

              const notification = {
                topic: 'cv.info.custom_visu_config_xsd_missing',
                target: 'popup',
                title: qx.locale.Manager.tr('Missing file "custom_visu_config.xsd"'),
                message: qx.locale.Manager.tr(
                  'The file "custom_visu_config.xsd" is missing in your config-folder. Do you want to create a default file now? This file is needed for full tile-config validation support.'
                ),
                unique: true,
                actions: {
                  link: [
                    {
                      title: qx.locale.Manager.tr('Ignore'),
                      type: 'ignore',
                      action(ev) {
                        if (qx.core.Environment.get('html.storage.local')) {
                          localStorage.setItem('cv.custom_visu_config_xsd_missing', 'true');
                        }
                      },
                      needsConfirmation: false
                    },
                    {
                      title: qx.locale.Manager.tr('Yes'),
                      type: 'copy',
                      action(ev) {
                         cv.io.rest.Client.getFsClient().copySync(
                          { src: 'resource/custom_visu_config.xsd', target: 'custom_visu_config.xsd', force: false},
                          function (err) {
                            if (err) {
                              this.error('Failed to copy default custom_visu_config.xsd', err);
                            }
                          },
                          this
                        );
                      },
                      needsConfirmation: false
                    }
                  ]
                }
              };
              if (cv.TemplateEngine.getInstance().isDomFinished()) {
                cv.core.notifications.Router.dispatchMessage(notification.topic, notification);
              } else {
                qx.event.message.Bus.subscribe(
                  'setup.dom.finished',
                  function () {
                    cv.core.notifications.Router.dispatchMessage(notification.topic, notification);
                  },
                  this
                );
              }
            }

            if (window.Sentry) {
              if ('server_release' in env) {
                Sentry.setTag('server.release', env.server_release);
              }
              if ('server_branch' in env) {
                Sentry.setTag('server.branch', env.server_branch);
              }
              if ('server_id' in env) {
                Sentry.setTag('server.id', env.server_id);
              }
            }
          }
        });
        xhr.addListener('statusError', e => {
          failedCheck(
            qx.locale.Manager.tr('Disabling manager due to failed PHP request querying the environment.'),
            qx.locale.Manager.tr('Failed PHP request querying the environment.')
          );
        });
        xhr.send();
      }
    },

    close() {
      this.setActive(false);
      const clients = cv.io.BackendConnections.getClients();
      for (const name in clients) {
        clients[name].terminate();
      }
    },

    /**
     * Install the service-worker if possible
     */
    registerServiceWorker() {
      if (cv.Config.useServiceWorker === true) {
        const workerFile = 'ServiceWorker.js';
        navigator.serviceWorker
          .register(workerFile)
          .then(
            function (reg) {
              this.debug('ServiceWorker successfully registered for scope ' + reg.scope);

              // configure service worker
              var configMessage = {
                command: 'configure',
                message: {
                  forceReload: cv.Config.forceReload,
                  debug: qx.core.Environment.get('qx.debug')
                }
              };

              if (reg.active) {
                reg.active.postMessage(configMessage);
              } else {
                navigator.serviceWorker.ready.then(function (ev) {
                  ev.active.postMessage(configMessage);
                });
              }
            }.bind(this)
          )
          .catch(
            function (err) {
              this.error('Error registering service-worker: ', err);
            }.bind(this)
          );
      } else if (navigator.serviceWorker) {
        navigator.serviceWorker.getRegistrations().then(
          function (registrations) {
            this.debug('unregistering existing service workers');
            registrations.forEach(function (registration) {
              registration.unregister();
            });
          }.bind(this)
        );
      }
    }
  }
});
